// analytics.js — Analytics & Tracking module (storefront side)
//
// One admin toggle turns tracking on/off site-wide; StoreLayout.js calls
// initAnalytics() once with whatever GET /content returned, and every page
// component calls trackEvent(name, params) at the moment a real business
// event happens (see Product.js, Cart.js, Register.js, Contact.js, etc.).
// No page ever needs its own <script> tag pasted in.
//
// GTM-primary rule: when Google Tag Manager is configured, this only ever
// loads the GTM container and pushes to window.dataLayer — GA4/Meta
// Pixel/Google Ads are expected to be wired up as tags INSIDE that GTM
// container instead, so the same event is never fired through two separate
// script loads at once. Direct GA4/Pixel/Ads scripts only load when GTM is
// not configured.

const PING_TYPES = ['ga4', 'gtm', 'meta_pixel', 'google_ads'];

// Meta's own standard-event vocabulary is different from GA4's — mapped
// once here so trackEvent() can speak both without every call site
// needing to know it. Events with no close Meta equivalent fall back to
// trackCustom with the original name.
const META_EVENT_MAP = {
  page_view: 'PageView',
  product_view: 'ViewContent',
  search: 'Search',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  purchase: 'Purchase',
  account_registration: 'CompleteRegistration',
  wholesale_enquiry: 'Lead',
  contact_form_submission: 'Contact',
  newsletter_signup: 'Subscribe',
};

let cfg = null;
let debugMode = false;
const debugSubscribers = new Set();

function ping(type) {
  if (!PING_TYPES.includes(type)) return;
  // Best-effort, fire-and-forget — backs the "Connected" status shown in
  // Admin > Website > Analytics & Tracking. A failed ping just means that
  // status won't update this visit; it never blocks or breaks tracking.
  fetch('/api/site/tracking-ping', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type }),
  }).catch(() => {});
}

function loadScript(src, id) {
  if (id && document.getElementById(id)) return; // never double-load on route changes
  const s = document.createElement('script');
  s.src = src; s.async = true; if (id) s.id = id;
  document.head.appendChild(s);
}

function logDebug(entry) {
  if (!debugMode) return;
  const line = { ...entry, at: new Date().toLocaleTimeString() };
  // eslint-disable-next-line no-console
  console.log('[tracking]', line.event || line.label, line);
  debugSubscribers.forEach(fn => fn(line));
}

// Google's Consent Mode v2 "default denied" signal — sent before any GA4/
// Ads config call regardless of which loads. This site doesn't have a
// cookie-consent banner yet (a separate piece of work), so tracking starts
// in the most privacy-conservative state Google defines; once a real
// consent banner exists, it should call gtag('consent','update', {...})
// when a visitor accepts, rather than this file being changed.
function sendConsentDefault() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag('consent', 'default', {
    analytics_storage: 'denied', ad_storage: 'denied',
    ad_user_data: 'denied', ad_personalization: 'denied',
  });
}

function bootGTM(containerId) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  loadScript(`https://www.googletagmanager.com/gtm.js?id=${containerId}`, 'gtm-script');
  if (!document.getElementById('gtm-noscript')) {
    const ns = document.createElement('noscript');
    ns.id = 'gtm-noscript';
    ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(ns, document.body.firstChild);
  }
  ping('gtm');
  logDebug({ type: 'boot', label: `GTM ${containerId} loaded — GA4/Pixel/Ads tags should be configured inside this container` });
}

function bootGA4(measurementId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`, 'ga4-script');
  window.gtag('js', new Date());
  // send_page_view:false — page_view is fired explicitly by StoreLayout's
  // own route-change tracking, matching every other event's instrumentation
  // instead of relying on gtag's own auto-pageview.
  window.gtag('config', measurementId, { send_page_view: false });
  ping('ga4');
  logDebug({ type: 'boot', label: `GA4 ${measurementId} loaded` });
}

function bootMetaPixel(pixelId) {
  if (window.fbq) { window.fbq('init', pixelId); ping('meta_pixel'); return; }
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', pixelId);
  ping('meta_pixel');
  logDebug({ type: 'boot', label: `Meta Pixel ${pixelId} loaded` });
}

function bootGoogleAds(conversionId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  // Shares one gtag.js load with GA4 when both are configured — Google Ads
  // and GA4 tags both run through the same script, just configured with
  // different IDs, so a second <script> tag would be a pure duplicate.
  if (!document.getElementById('ga4-script')) {
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${conversionId}`, 'gads-script');
  }
  window.gtag('js', new Date());
  window.gtag('config', conversionId);
  ping('google_ads');
  logDebug({ type: 'boot', label: `Google Ads ${conversionId} loaded` });
}

function injectCustomScripts(html, placement) {
  const holder = document.createElement('div');
  holder.innerHTML = html;
  const target = placement === 'body_end' ? document.body : document.head;
  // innerHTML-parsed <script> elements never execute on their own — each
  // one has to be re-created and re-appended for the browser to run it.
  Array.from(holder.childNodes).forEach(node => {
    if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
      const s = document.createElement('script');
      Array.from(node.attributes).forEach(a => s.setAttribute(a.name, a.value));
      s.text = node.textContent;
      target.appendChild(s);
    } else {
      target.appendChild(node.cloneNode(true));
    }
  });
  logDebug({ type: 'boot', label: `Custom tracking script(s) injected into <${placement === 'body_end' ? '/body' : 'head'}>` });
}

/** Call once, on app mount, with the `tracking` object GET /content returns. */
export function initAnalytics(trackingConfig) {
  cfg = trackingConfig || null;
  debugMode = new URLSearchParams(window.location.search).get('tracking_debug') === '1';
  if (debugMode) mountDebugPanel();
  if (!cfg) { logDebug({ type: 'boot', label: 'No tracking configured (Admin > Website > Analytics & Tracking)' }); return; }

  sendConsentDefault();

  if (cfg.gtm_active) {
    bootGTM(cfg.gtm_container_id);
  } else {
    if (cfg.ga4_measurement_id) bootGA4(cfg.ga4_measurement_id);
    if (cfg.meta_pixel_id) bootMetaPixel(cfg.meta_pixel_id);
    if (cfg.google_ads_conversion_id) bootGoogleAds(cfg.google_ads_conversion_id);
  }

  if (cfg.custom_scripts) injectCustomScripts(cfg.custom_scripts, cfg.custom_scripts_placement);
}

/** Call at the moment a real business event happens — see the 14 events in analyticsSettings.js. */
export function trackEvent(name, params = {}) {
  if (!cfg) { logDebug({ type: 'event', event: name, params, destinations: ['(tracking not configured)'] }); return; }
  const destinations = [];

  if (cfg.gtm_active) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...params });
    destinations.push('GTM dataLayer');
  } else {
    if (cfg.ga4_measurement_id && window.gtag) {
      window.gtag('event', name, params);
      destinations.push('GA4');
    }
    if (cfg.meta_pixel_id && window.fbq) {
      const metaEvent = META_EVENT_MAP[name];
      if (metaEvent) { window.fbq('track', metaEvent, params); destinations.push(`Meta Pixel (${metaEvent})`); }
      else { window.fbq('trackCustom', name, params); destinations.push('Meta Pixel (custom)'); }
    }
    if (cfg.google_ads_conversion_id && cfg.google_ads_conversion_labels?.[name] && window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: `${cfg.google_ads_conversion_id}/${cfg.google_ads_conversion_labels[name]}`,
        ...params,
      });
      destinations.push('Google Ads (conversion)');
    }
  }

  logDebug({ type: 'event', event: name, params, destinations: destinations.length ? destinations : ['(no destination configured for this event)'] });
}

// ── Debug overlay — only ever mounted when the page URL carries
// ?tracking_debug=1 (see the "Preview Site (Debug Mode)" button in the
// Analytics & Tracking admin page). Shows every trackEvent() call live,
// with its payload and which platform(s) it actually reached, so an admin
// can click around the real site and see real instrumentation firing —
// not a synthetic test.
function mountDebugPanel() {
  if (document.getElementById('tracking-debug-panel')) return;
  const panel = document.createElement('div');
  panel.id = 'tracking-debug-panel';
  panel.style.cssText = 'position:fixed;bottom:0;right:0;width:380px;max-width:92vw;max-height:46vh;overflow-y:auto;background:#0f1b2d;color:#d7e4f2;font:12px/1.5 "IBM Plex Mono",monospace;z-index:999999;border-top-left-radius:10px;box-shadow:0 -4px 24px rgba(0,0,0,.4)';
  panel.innerHTML = '<div style="position:sticky;top:0;padding:8px 12px;background:#14233b;font-weight:700;display:flex;justify-content:space-between;align-items:center">🔍 Tracking Debug <span id="tdp-count" style="opacity:.6">(0)</span></div><div id="tdp-log" style="padding:8px 12px"></div>';
  document.body.appendChild(panel);
  let count = 0;
  debugSubscribers.add(line => {
    count++;
    const counter = document.getElementById('tdp-count');
    if (counter) counter.textContent = `(${count})`;
    const log = document.getElementById('tdp-log');
    if (!log) return;
    const row = document.createElement('div');
    row.style.cssText = 'margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid #223250';
    const dest = line.destinations ? line.destinations.join(', ') : '';
    const paramsStr = line.params && Object.keys(line.params).length ? JSON.stringify(line.params) : '';
    row.innerHTML = `<div style="color:#7fd8cb">${line.at} — ${line.event || line.label}</div>`
      + (dest ? `<div style="opacity:.75">→ ${dest}</div>` : '')
      + (paramsStr ? `<div style="opacity:.5;word-break:break-all">${paramsStr}</div>` : '');
    log.prepend(row);
  });
}
